import { Component, inject, OnDestroy, OnInit, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';

import { AufnahmePunktDocument, istPunktartMitProtokoll, punktartBeschreibung } from '../../data-model';
import { Command, NavbarService } from 'src/app/navbar.service';
import { AuthService } from 'src/app/auth.service';
import { DataService } from 'src/app/data.service';
import { FileExportService, SelectionService, ViewportService, ConfirmService } from '@luzula/luzula-lib';
import { PdfOptionen, ProtokollPdfService } from './protokoll-pdf.service';
import { dbFehlerText } from 'src/app/alle-bohrpunkte/db-meldung';
import { ohnePersonenangaben, personenangabenVerbergen } from '../personenangaben';

export type SortMode = 'nummer' | 'datum' | 'forstamtRevier' | 'jahr' | 'auswahl';

@Component({
    standalone: false,
	selector: 'bp-multi-print-view',
	templateUrl: './multi-print-view.component.html',
	styleUrls: ['./multi-print-view.component.css'],

})
export class MultiPrintViewComponent implements OnInit, OnDestroy {
	@ViewChild('printContent') printContent!: ElementRef;

	bps: AufnahmePunktDocument[] = [];
	loading = true;
	/**
	 * Warum keine Punkte da sind, obwohl welche angefordert wurden.
	 *
	 * Nutzer-Befund 24.09.2026: Die Sammelabfrage war vom Server abgewiesen
	 * worden (Anmeldung trug nicht), der Fehler ging nur in die Konsole, und die
	 * Ansicht behauptete „Keine Punkte zum Drucken ausgewählt". Der Nutzer suchte
	 * den Fehler bei der Auswahl. Steht hier ein Satz, zeigt die Ansicht ihn
	 * statt der Meldung zur fehlenden Auswahl.
	 */
	ladeFehler: string | null = null;
	pdfExporting = false;
	pdfPreviewUrl: SafeResourceUrl | null = null;
	private pdfBlobUrl: string | null = null;

	/** Originale Auswahlreihenfolge (für sortMode='auswahl') */
	private auswahlOrder = new Map<string, number>();

	/** Sortierkriterien in Prioritätsreihenfolge (erstes = höchste Priorität). */
	sortKeys: SortMode[] = ['nummer'];
	/** 0 = alle in eine PDF; sonst Punkte pro Datei */
	chunkSize = 0;
	/** Wenn true: nur Punkte mit Langprotokoll exportieren, Kurzprotokoll-Punkte ausblenden. */
	nurLangprotokoll = false;
	/**
	 * Hinter jedes Protokoll eine Seite mit dem Profilbild und der Erlaeuterung,
	 * wie es entsteht (Nutzer-Wunsch 25.09.2026). Aus als Vorgabe: Das Bild
	 * macht die Datei je Punkt um einige hundert Kilobyte schwerer, und wer es
	 * nicht braucht, bekommt den Export wie bisher.
	 */
	mitProfilbild = false;

	readonly availableSortKeys: { key: SortMode, label: string }[] = [
		{ key: 'nummer', label: 'Bohrpunktnummer' },
		{ key: 'forstamtRevier', label: 'Forstamt / Revier' },
		{ key: 'jahr', label: 'Jahr' },
		{ key: 'datum', label: 'Datum' },
		{ key: 'auswahl', label: 'Auswahlreihenfolge' },
	];

	private previewDebounce?: number;

	istPunktartMitProtokoll = istPunktartMitProtokoll;

	private queryParamsSubscription?: Subscription;
	private authChangedSubscription?: Subscription;
	ds = inject(DataService);
	vps = inject(ViewportService);
	private aus = inject(AuthService);
	private ns = inject(NavbarService);
	private route = inject(ActivatedRoute);
	private router = inject(Router);
	private selectionService = inject(SelectionService);
	private fileExport = inject(FileExportService);
	private pdfService = inject(ProtokollPdfService);
	/** Beim Laden gesetzt: Personenangaben fuer diesen Nutzer verborgen (Datennutzer, nicht angemeldet). */
	private personenVerborgen = false;
	private sanitizer = inject(DomSanitizer);
	private confirmService = inject(ConfirmService);

	private initButtons() {
		this.ns.subscribe(Command.goBackToPoint, () => this.onClose());
		this.ns.subscribe(Command.print, () => this.onPrint());
	}

	async ngOnInit() {
		this.queryParamsSubscription = this.route.queryParams.subscribe(async params => {
			const idsParam = params['ids'];
			if (idsParam) {
				const ids = idsParam.split(',');
				await this.loadPoints(ids);
			}
		});
		this.authChangedSubscription = this.aus.authChanged.subscribe(() => this.initButtons());
		this.initButtons();
	}

	private async loadPoints(ids: string[]) {
		this.loading = true;
		this.bps = [];
		this.ladeFehler = null;
		this.auswahlOrder.clear();
		ids.forEach((id, i) => this.auswahlOrder.set(id, i));

		try {
			// Alle Punkte in einem Batch laden (eine Anfrage statt vieler)
			// KEIN WMS-Fetch für Massenansicht — WMS-Daten werden beim Einzelaufruf gespeichert (Auto-Save)
			const docs = await this.ds.getBohrpunkteBatch(ids);
			// Datennutzer/Betrachter: Kartierer und Aufnahmedatum gehen weder ins PDF
			// noch in die Dateinamen (Nutzer-Befund 06.09.2026, Gastmodus). Kopie,
			// damit das Original unberuehrt bleibt.
			this.personenVerborgen = personenangabenVerbergen(this.aus);
			this.bps = docs
				.map(bp => this.ds.updateBohrpunkt(bp, false))
				.map(bp => this.personenVerborgen ? ohnePersonenangaben(bp) : bp);
			this.applySort();
		} catch (e) {
			// Die technische Fassung bleibt in der Konsole; die Ansicht sagt den
			// Grund in Worten (abgewiesen, Server-Fehler, keine Antwort).
			console.error('Failed to load points:', e);
			this.ladeFehler = `Die Punkte konnten nicht geladen werden. ${dbFehlerText(e)}`;
		}
		if (ids.length > 0 && this.bps.length === 0 && !this.ladeFehler) {
			// Kein Fehler, aber auch nichts da: Die Kennungen liegen nicht vor, oder
			// die Datenbank stand gar nicht bereit (dann liefert die Sammelabfrage
			// still eine leere Liste). Beides ist keine fehlende Auswahl.
			this.ladeFehler = 'Die Punkte konnten nicht geladen werden. Sie liegen nicht vor, oder die Verbindung zur Datenbank steht nicht.';
		}

		this.loading = false;

		// PDF-Vorschau generieren (nur auf Desktop, nicht auf mobilen Geräten)
		if (this.bps.length > 0 && !this.vps.isMobile) {
			await this.generatePdfPreview();
		}
	}

	onSortKeyChange(index: number, key: SortMode) {
		if (this.sortKeys[index] === key) return;
		this.sortKeys = this.sortKeys.map((k, i) => i === index ? key : k);
		this.applySort();
		this.schedulePreview();
	}

	onAddSortKey() {
		const next = this.availableSortKeys.find(s => !this.sortKeys.includes(s.key));
		if (!next) return;
		this.sortKeys = [...this.sortKeys, next.key];
		this.applySort();
		this.schedulePreview();
	}

	onRemoveSortKey(index: number) {
		if (this.sortKeys.length <= 1) return;
		this.sortKeys = this.sortKeys.filter((_, i) => i !== index);
		this.applySort();
		this.schedulePreview();
	}

	get canAddSortKey(): boolean {
		return this.sortKeys.length < this.availableSortKeys.length;
	}

	trackByIndex(i: number): number {
		return i;
	}

	/** Mögliche Auswahl für ein Dropdown: alle, die noch nicht woanders gewählt sind, plus der eigene. */
	availableForSlot(index: number): { key: SortMode, label: string }[] {
		const inUse = new Set(this.sortKeys.filter((_, i) => i !== index));
		return this.availableSortKeys.filter(s => !inUse.has(s.key));
	}

	onChunkSizeChange(size: number) {
		const sane = Math.max(0, Math.floor(Number(size) || 0));
		if (sane === this.chunkSize) return;
		this.chunkSize = sane;
		this.schedulePreview();
	}

	onFilterToggle(value: boolean) {
		if (value === this.nurLangprotokoll) return;
		this.nurLangprotokoll = value;
		this.schedulePreview();
	}

	onProfilbildToggle(value: boolean) {
		if (value === this.mitProfilbild) return;
		this.mitProfilbild = value;
		this.schedulePreview();
	}

	/** Was der Export bekommt - fuer Vorschau und Speichern dieselbe Wahl. */
	private pdfOptionen(): PdfOptionen {
		return { mitProfilbild: this.mitProfilbild };
	}

	/** Effektive Punkte für Vorschau/Export: optional auf Langprotokoll-Punkte gefiltert. */
	private effectiveBps(): AufnahmePunktDocument[] {
		if (!this.nurLangprotokoll) return this.bps;
		return this.bps.filter(bp => istPunktartMitProtokoll(bp.type));
	}

	get effectiveCount(): number {
		return this.effectiveBps().length;
	}

	get isFiltered(): boolean {
		return this.nurLangprotokoll && this.effectiveCount !== this.bps.length;
	}

	/** Bei Sort/Chunk-Änderung Vorschau neu erzeugen — debounced, um nicht bei jeder Tasteneingabe zu rendern. */
	private schedulePreview() {
		if (this.vps.isMobile || this.bps.length === 0) return;
		if (this.previewDebounce) window.clearTimeout(this.previewDebounce);
		this.previewDebounce = window.setTimeout(() => this.generatePdfPreview(), 250);
	}

	private applySort() {
		const keys = this.sortKeys;
		const orderMap = this.auswahlOrder;
		this.bps = [...this.bps].sort((a, b) => {
			for (const key of keys) {
				const c = compareByKey(a, b, key, orderMap);
				if (c !== 0) return c;
			}
			return 0;
		});
	}

	/** Punkte für die Vorschau — erstes Chunk gemäß Typ/Kartierer-Gruppierung und Chunk-Größe. */
	private previewBps(): AufnahmePunktDocument[] {
		return this.buildChunks()[0]?.bps ?? this.bps;
	}

	get hasMultipleChunks(): boolean {
		return this.buildChunks().length > 1;
	}

	get chunkCount(): number {
		return Math.max(1, this.buildChunks().length);
	}

	get previewCount(): number {
		return this.buildChunks()[0]?.bps.length ?? this.bps.length;
	}

	private async generatePdfPreview() {
		try {
			const pdfBlob = await this.pdfService.generatePdf(this.previewBps(), this.pdfOptionen());
			// Alte URL freigeben
			if (this.pdfBlobUrl) {
				URL.revokeObjectURL(this.pdfBlobUrl);
			}
			this.pdfBlobUrl = URL.createObjectURL(pdfBlob);
			this.pdfPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.pdfBlobUrl);
		} catch (e) {
			console.error('PDF preview generation failed:', e);
		}
	}

	ngOnDestroy(): void {
		this.ns.unsubscribe(Command.goBackToPoint);
		this.ns.unsubscribe(Command.print);
		this.queryParamsSubscription?.unsubscribe();
		this.authChangedSubscription?.unsubscribe();
		if (this.previewDebounce) window.clearTimeout(this.previewDebounce);
		// Blob-URL freigeben
		if (this.pdfBlobUrl) {
			URL.revokeObjectURL(this.pdfBlobUrl);
		}
	}

	onClose() {
		// Waehrend eines laufenden Exports nicht wegnavigieren -- sonst bricht der Export ab.
		if (this.pdfExporting) return;
		this.router.navigate(['/']);
	}

	onPrint() {
		window.print();
	}

	async onSavePdf() {
		if (this.effectiveCount === 0 || this.pdfExporting) return;

		const chunks = this.buildChunks();

		if (chunks.length === 1) {
			await this.saveSinglePdf(chunks[0].bps, chunks[0].suggestedName);
			return;
		}

		await this.saveMultiplePdfs(chunks);
	}

	/**
	 * Teilt this.bps in Chunks auf und vergibt sprechende Dateinamen.
	 * Schritt 1: Gruppierung nach (Punktart, Kartierer) — verschiedene Typen ODER Kartierer → verschiedene Dateien,
	 *            Reihenfolge der Gruppen folgt dem ersten Auftreten in der sortierten Liste.
	 * Schritt 2: Pro Gruppe wird ggf. weiter nach chunkSize aufgeteilt.
	 * Dateinamen: `<Punktart>_<Kartierer>_<Range>.pdf` (Kartierer entfällt, wenn das Feld leer ist).
	 */
	private buildChunks(): { bps: AufnahmePunktDocument[], suggestedName: string }[] {
		const source = this.effectiveBps();
		if (source.length === 0) return [];

		// Nach (Typ, Kartierer) gruppieren. \x00 als Trenner ist kollisionssicher.
		const groups = new Map<string, { type: string, kartierer: string, bps: AufnahmePunktDocument[] }>();
		for (const bp of source) {
			const t = bp.type || '';
			// Verborgene Personenangaben: keine Gruppe je Person, kein Name im Dateinamen.
			const k = this.personenVerborgen ? '' : (bp.header?.aufnahme?.kartierer?.trim() || '');
			const key = `${t}\x00${k}`;
			let g = groups.get(key);
			if (!g) { g = { type: t, kartierer: k, bps: [] }; groups.set(key, g); }
			g.bps.push(bp);
		}

		const result: { bps: AufnahmePunktDocument[], suggestedName: string }[] = [];
		for (const g of groups.values()) {
			const typeName = sanitizeFilename(shortenPunktart(g.type));
			const kartiererPart = g.kartierer ? `_${sanitizeFilename(g.kartierer)}` : '';
			const groupTotal = g.bps.length;

			if (this.chunkSize <= 0 || groupTotal <= this.chunkSize) {
				const range = this.rangeLabel(g.bps, 0, groupTotal);
				result.push({ bps: g.bps, suggestedName: `${typeName}${kartiererPart}_${range}.pdf` });
				continue;
			}
			for (let i = 0; i < groupTotal; i += this.chunkSize) {
				const slice = g.bps.slice(i, i + this.chunkSize);
				const range = this.rangeLabel(slice, i, groupTotal);
				result.push({ bps: slice, suggestedName: `${typeName}${kartiererPart}_${range}.pdf` });
			}
		}
		return result;
	}

	/**
	 * Label für einen Chunk: laufende Nummer (lfdNr) des ersten und letzten Punkts MIT Nummer.
	 * Punkte ohne Nummer (leer/fehlend) werden bei der Range-Bestimmung übersprungen, damit ein
	 * einzelner unnummerierter Punkt nicht den ganzen Dateinamen auf den Index-Fallback kippt.
	 * Fallback (kein einziger Punkt im Chunk hat eine Nummer): gepolsterter Index (1-basiert).
	 */
	private rangeLabel(slice: AufnahmePunktDocument[], offset: number, total: number): string {
		if (slice.length === 0) return '';

		let first = '';
		for (const bp of slice) {
			const lbl = lfdLabel(bp);
			if (lbl) { first = lbl; break; }
		}
		let last = '';
		for (let i = slice.length - 1; i >= 0; i--) {
			const lbl = lfdLabel(slice[i]);
			if (lbl) { last = lbl; break; }
		}

		if (first && last) {
			return first === last
				? sanitizeFilename(first)
				: `${sanitizeFilename(first)}-${sanitizeFilename(last)}`;
		}

		// Fallback: gepolsterter Index
		const pad = String(total).length;
		const from = String(offset + 1).padStart(pad, '0');
		const to = String(offset + slice.length).padStart(pad, '0');
		return `${from}-${to}`;
	}

	private async saveSinglePdf(bps: AufnahmePunktDocument[], filename: string) {
		let fileHandle: any = null;
		if ('showSaveFilePicker' in window) {
			try {
				fileHandle = await (window as any).showSaveFilePicker({
					suggestedName: filename,
					types: [{ description: 'PDF-Dokument', accept: { 'application/pdf': ['.pdf'] } }]
				});
			} catch (e: any) {
				if (e.name === 'AbortError') return;
				console.warn('Save picker nicht verfügbar:', e);
			}
		}

		this.pdfExporting = true;
		try {
			const pdfBlob = await this.pdfService.generatePdf(bps, this.pdfOptionen());
			if (fileHandle) {
				const writable = await fileHandle.createWritable();
				await writable.write(pdfBlob);
				await writable.close();
				await this.confirmService.alert('PDF wurde gespeichert.', 'Export erfolgreich');
			} else {
				this.fileExport.download({ filename, mimeType: 'application/pdf', content: pdfBlob });
				await this.confirmService.alert('PDF wurde heruntergeladen.', 'Export erfolgreich');
			}
		} catch (e) {
			console.error('PDF export failed:', e);
			alert('PDF-Export fehlgeschlagen. Bitte versuchen Sie es mit der Drucken-Funktion.');
		} finally {
			this.pdfExporting = false;
		}
	}

	private async saveMultiplePdfs(chunks: { bps: AufnahmePunktDocument[], suggestedName: string }[]) {
		// Bevorzugt: Ordner-Picker (Chrome/Edge). Fallback: einzelne Downloads.
		let dirHandle: any = null;
		if ('showDirectoryPicker' in window) {
			try {
				dirHandle = await (window as any).showDirectoryPicker({ mode: 'readwrite' });
			} catch (e: any) {
				if (e.name === 'AbortError') return;
				console.warn('Directory picker nicht verfügbar:', e);
			}
		}

		this.pdfExporting = true;
		let written = 0;
		try {
			for (const chunk of chunks) {
				const pdfBlob = await this.pdfService.generatePdf(chunk.bps, this.pdfOptionen());
				if (dirHandle) {
					const fh = await dirHandle.getFileHandle(chunk.suggestedName, { create: true });
					const writable = await fh.createWritable();
					await writable.write(pdfBlob);
					await writable.close();
				} else {
					this.fileExport.download({ filename: chunk.suggestedName, mimeType: 'application/pdf', content: pdfBlob });
				}
				written++;
			}
			const target = dirHandle ? 'in den gewählten Ordner' : 'als Einzeldownloads';
			await this.confirmService.alert(`${written} PDFs wurden ${target} gespeichert.`, 'Export erfolgreich');
		} catch (e) {
			console.error('PDF export failed:', e);
			alert(`PDF-Export fehlgeschlagen nach ${written} von ${chunks.length} Dateien.`);
		} finally {
			this.pdfExporting = false;
		}
	}
}

// --------------------------------------------------------------------------
// Bohrpunktnummer-Parser (Schema FA-FR-lfdNr-Jahr, vgl. aufnahme.component.ts:88)
// --------------------------------------------------------------------------

interface ParsedNummer {
	fa: number | null;
	fr: number | null;
	lfdNr: string;  // bleibt String, weil Buchstabensuffixe wie "12a" erlaubt sind
	jahr: number | null;
	raw: string;
}

/** Beschreibung der Punktart (Single Source aus der Punktarten-Liste). Pendant zu ProtokollPdfService.shortenPunktart. */
export function shortenPunktart(type: string | undefined): string {
	if (!type) return 'Bohrpunkte';
	return punktartBeschreibung(type) || type;
}

/**
 * Liefert die laufende Nummer (lfdNr) eines Punkts als String, geeignet als Dateinamen-Bestandteil.
 * Fällt bei unparsbarem Schema auf die rohe header.nummer zurück. Leerstring wenn keine Nummer.
 */
export function lfdLabel(bp: AufnahmePunktDocument): string {
	const parsed = parseBohrpunktNummer(bp.header?.nummer);
	if (parsed.fa !== null) return parsed.lfdNr;
	return parsed.raw;
}

/** Macht einen String dateinamen-tauglich (Windows/macOS/Linux): ersetzt verbotene Zeichen durch '_'. */
export function sanitizeFilename(name: string): string {
	// eslint-disable-next-line no-control-regex
	return name.replace(/[\\/:*?"<>|\x00-\x1f]/g, '_').trim() || '_';
}

/**
 * Zerlegt eine Nummer im Schema FA-FR-lfdNr-Jahr. lfdNr darf selbst '-' enthalten ist hier nicht vorgesehen.
 * Manche Punkte tragen ihre Nummer als Zahl; sie wird wie Text behandelt (Befund 29.09.2026).
 */
export function parseBohrpunktNummer(nummer: string | number | undefined | null): ParsedNummer {
	const raw = String(nummer ?? '').trim();
	if (!raw) return { fa: null, fr: null, lfdNr: '', jahr: null, raw: '' };
	// Greedy auf vier Segmente: ^(FA)-(FR)-(lfdNr)-(Jahr)$
	const m = raw.match(/^(\d{1,3})-(\d{1,3})-(.+)-(\d{4})$/);
	if (!m) {
		return { fa: null, fr: null, lfdNr: raw, jahr: null, raw };
	}
	return {
		fa: Number(m[1]),
		fr: Number(m[2]),
		lfdNr: m[3],
		jahr: Number(m[4]),
		raw,
	};
}

/**
 * Vergleicht zwei Bohrpunkte für genau ein Sortierkriterium (atomar).
 * Mehrere Kriterien werden vom Aufrufer per `||` verkettet (siehe applySort).
 *
 * - 'nummer': nur die laufende Nummer (numerisch, mit Buchstabensuffix wie '12a')
 * - 'forstamtRevier': FA dann FR (aus dem Nummern-Schema)
 * - 'jahr': Jahr aus dem Nummern-Schema
 * - 'datum': Aufnahmedatum aufsteigend
 * - 'auswahl': ursprüngliche Auswahlreihenfolge
 *
 * Werte ohne Information landen ans Ende (undefined → MAX).
 */
export function compareByKey(
	a: AufnahmePunktDocument,
	b: AufnahmePunktDocument,
	key: SortMode,
	auswahlOrder: Map<string, number>,
): number {
	switch (key) {
		case 'auswahl': {
			const ai = auswahlOrder.get((a as any)._id) ?? Number.MAX_SAFE_INTEGER;
			const bi = auswahlOrder.get((b as any)._id) ?? Number.MAX_SAFE_INTEGER;
			return ai - bi;
		}
		case 'datum': {
			const da = String(a.header?.aufnahme?.datum ?? '');
			const db = String(b.header?.aufnahme?.datum ?? '');
			if (!da && !db) return 0;
			if (!da) return 1;
			if (!db) return -1;
			return da.localeCompare(db);
		}
		case 'nummer': {
			const pa = parseBohrpunktNummer(a.header?.nummer);
			const pb = parseBohrpunktNummer(b.header?.nummer);
			// Beide ohne parsbares Schema → roher String-Vergleich
			if (pa.fa === null && pb.fa === null) {
				return pa.raw.localeCompare(pb.raw, undefined, { numeric: true });
			}
			if (pa.fa === null) return 1;
			if (pb.fa === null) return -1;
			return pa.lfdNr.localeCompare(pb.lfdNr, undefined, { numeric: true });
		}
		case 'forstamtRevier': {
			const pa = parseBohrpunktNummer(a.header?.nummer);
			const pb = parseBohrpunktNummer(b.header?.nummer);
			const fa = (pa.fa ?? Number.MAX_SAFE_INTEGER) - (pb.fa ?? Number.MAX_SAFE_INTEGER);
			if (fa !== 0) return fa;
			return (pa.fr ?? Number.MAX_SAFE_INTEGER) - (pb.fr ?? Number.MAX_SAFE_INTEGER);
		}
		case 'jahr': {
			const pa = parseBohrpunktNummer(a.header?.nummer);
			const pb = parseBohrpunktNummer(b.header?.nummer);
			return (pa.jahr ?? Number.MAX_SAFE_INTEGER) - (pb.jahr ?? Number.MAX_SAFE_INTEGER);
		}
	}
}
